<template>
  <transition name="animation">
    <div>
      <div class="container d-flex justify-content-center" v-if="show">
        <div class="tip d-flex align-items-center text-medium-1">
          <div class="d-flex align-items-center">
            <i class="clock-icon mr-1"></i>
            <span class="text-error bold">You have check-ins due</span>
          </div>
          <b-button
            variant="link"
            class="text-primary text-medium-1 checkin-button"
            @click="onCheckinClick"
          >
            Quickly check-in using the <i>Beast</i> mode
          </b-button>
          <span class="kb-shortcut">b</span>
          <b-button variant="link" class="text-primary text-medium-1 snooze-button" @click="snooze">
            Snooze
          </b-button>
        </div>
      </div>
      <bulk-check-in
        @checkin-posted="$emit('checkin-posted')"
        v-if="isBulkCheckinModalShown"
        v-model="isBulkCheckinModalShown"
        :org-id="orgId"
      />
    </div>
  </transition>
</template>

<script>
import { trackEvent } from '@/analytics'
import { EVENT_ACTION, EVENT_CATEGORY, EVENT_NAME } from '@/constants/analytics'
import { defaultInterval, intervalWithId, nowInterval } from '@/interval'
import * as R from 'ramda'
import { BButton } from 'bootstrap-vue'
import { STORAGE_KEYS } from '@/constants/storage'
import { MILLIS_IN_A_DAY } from '@/utils/formatDate'
import BulkCheckIn from '@/components/BulkCheckIn'

/**
 * this component shows a tip to perfomr bulk check-ins if check-ins are overdue
 *
 * @emits
 * - checkin-posted
 */
export default {
  name: 'CheckinReminderBar',
  props: ['goals', 'orgId', 'source'],
  components: {
    BButton,
    BulkCheckIn
  },
  data() {
    return {
      show: this.evaluateShow(),
      isBulkCheckinModalShown: false
    }
  },
  methods: {
    getSelectedInterval() {
      const interval = this.$route.query.interval
      if (interval) {
        return intervalWithId(interval)
      } else {
        return defaultInterval()
      }
    },
    snooze() {
      localStorage.setItem(STORAGE_KEYS.checkinSnoozedAt, new Date().getTime())
      this.show = false
      trackEvent(
        EVENT_CATEGORY.NOTIFICATION,
        EVENT_ACTION.CLICK_CHECKIN_REMINDER,
        EVENT_NAME.SNOOZE
      )
    },
    revalidateShowBehaviour() {
      this.show = this.evaluateShow()
    },
    evaluateShow() {
      const snoozedAt = localStorage.getItem(STORAGE_KEYS.checkinSnoozedAt)
      let show = true
      if (snoozedAt && new Date().getTime() - +snoozedAt < MILLIS_IN_A_DAY) {
        show = false
      }
      const isCheckinOverdue = R.flatten(this.goals.map(goal => goal.keyResults))
        .filter(x => !x.isArchived)
        .some(R.prop('isCheckinOverdue'))
      return this.getSelectedInterval()?.id === nowInterval().id && isCheckinOverdue && show
    },
    /**
     * this function is getting called from outer scope as well for KB shortcut handling
     */
    showCheckInModal() {
      this.isBulkCheckinModalShown = true
    },
    onCheckinClick() {
      this.showCheckInModal()
      trackEvent(
        EVENT_CATEGORY.NOTIFICATION,
        EVENT_ACTION.CLICK_CHECKIN_REMINDER,
        EVENT_NAME.BULK_CHECK_IN
      )
    }
  },
  watch: {
    goals: 'revalidateShowBehaviour'
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/_styleConfigurations.scss';
@import '~@/styles/animations/fade';

.tip {
  background: $notificationBG;
  border-radius: 20px;
  padding: 0 20px;
  margin-bottom: 30px;
}

.snooze-button {
  margin-left: 25px;
}

.animation-enter-active,
.animation-leave-active,
.animation-leave-to {
  transition: all 300ms ease-out;
  max-height: 150px;
}
.animation-enter,
.animation-leave-to {
  opacity: 0;
  max-height: 0;
}
.checkin-button {
  position: relative;
  margin-left: 10px;
  &:before {
    content: '-';
    position: absolute;
    left: -2px;
  }
}

.kb-shortcut {
  background: $documentColor;
  padding: 0px 5px;
  border-radius: 3px;
  color: $primary;
}
@include media-breakpoint-down(md) {
  .tip {
    flex-direction: column;
    padding-top: 15px;
    width: 100%;
    padding-bottom: 5px;
    margin-bottom: 15px;
  }
  .snooze-button {
    margin-left: 0;
  }
  .checkin-button {
    margin-left: 0;

    &:before {
      display: none;
    }
  }
}
</style>
